import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { NotificationService } from './core.notification.service';

/**
 * GfTitleService — the single authority for setting the browser tab title.
 *
 * ## Standard Title Format
 *
 *   <Section> - <Subsection / Module> - <Item>
 *
 * ## Responsibilities
 *
 * 1. Set `document.title` via Angular's Title service.
 * 2. Broadcast the new title via NotificationService so other widgets
 *    (e.g. Favorites) can react without polling or reading the DOM.
 *
 * PageContextService acts as a **fallback** — it builds a structured title
 * from ui-router transition data only when the window title does not already
 * conform to the standard format.
 */
@Injectable()
export class GfTitleService extends Title {

    static readonly SEPARATOR = ' - ';
    static readonly TITLE_CHANGED_EVENT = 'TITLE_CHANGED';

    constructor(
        @Inject(DOCUMENT) doc: any,
        private notificationService: NotificationService
    ) {
        super(doc);
    }

    /**
     * Set the page title from a single pre-formatted string, or from
     * two / three ordered segments joined with " - ".
     *
     *   setTitle('Site Admin - Users')
     *   setTitle('Site Admin', 'Users')
     *   setTitle('GForge', 'Wiki', 'HomePage')
     */
    setTitle(newTitle: string): void;
    setTitle(section: string, subsection: string, item?: string): void;
    setTitle(sectionOrTitle: string, subsection?: string, item?: string): void {
        let title: string;
        if (subsection) {
            const segments = [sectionOrTitle, subsection, item]
                .map(segment => this.normalizeSegment(segment))
                .filter(Boolean);
            title = segments.join(GfTitleService.SEPARATOR);
        } else {
            title = this.normalize(sectionOrTitle);
        }
        super.setTitle(title);
        this.notificationService.notifyAll(GfTitleService.TITLE_CHANGED_EVENT, title);
    }

    isStandardFormat(title?: string): boolean {
        const t = title ?? this.getTitle();
        return this.getSegments(t).length >= 2;
    }

    getSegments(title?: string): string[] {
        const t = title ?? this.getTitle();
        return t.split(GfTitleService.SEPARATOR)
            .map(s => s.trim())
            .filter(Boolean);
    }

    private normalize(raw: string): string {
        const normalized = this.normalizeSegment(raw);
        if (!normalized) return normalized;
        return normalized.replace(/\s*-\s*/g, GfTitleService.SEPARATOR).trim();
    }

    /**
     * Coerce an arbitrary title segment into a trimmed string.
     *
     * This guards title assembly against non-string values from API payloads
     * and avoids runtime errors when callers pass numbers, objects or nullish values.
     */
    private normalizeSegment(raw: any): string {
        if (raw === null || raw === undefined) return '';
        if (typeof raw === 'string') return raw.trim();
        if (typeof raw === 'number' || typeof raw === 'boolean') {
            return `${raw}`.trim();
        }

        // Ignore objects/functions to avoid leaking implementation strings
        // (for example function source or "[object Object]") into the page title.
        return '';
    }
}
